import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { MeResponse, MembershipSummary, Permission } from '@brechoflow/contracts';
import { http, isApiError, setSessionExpiredHandler } from '@/lib/api';
import { authApi } from './auth-api';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface Session {
  status: SessionStatus;
  me: MeResponse | null;
  /** Vínculo (loja + papel) ativo no momento. */
  membership: MembershipSummary | null;
  permissions: ReadonlySet<Permission>;
  refetch: () => Promise<unknown>;
  /** Substitui os dados da sessão (após login, registro, troca de loja). */
  setMe: (me: MeResponse | null) => void;
  logout: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
}

export const SessionContext = createContext<Session | null>(null);

export const ME_KEY = ['me'] as const;

const EMPTY: ReadonlySet<Permission> = new Set();

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await http.get<MeResponse>('/auth/me');
      } catch (e) {
        if (isApiError(e) && e.isUnauthorized) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const clearAll = useCallback(() => {
    // só há o que limpar se havia sessão. Visitante anônimo (landing, loja, login): o 401 de /auth/me e a renovação que falha
    // também chegam aqui, e remover as consultas em andamento deixava páginas públicas (ex.: planos) presas no esqueleto.
    if (qc.getQueryData(ME_KEY)) {
      // não usar qc.clear(): derrubaria a própria consulta de /auth/me em andamento
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
    }
    qc.setQueryData(ME_KEY, null);
  }, [qc]);

  // refresh falhou em qualquer chamada: a sessão acabou
  useEffect(() => {
    setSessionExpiredHandler(clearAll);
    return () => setSessionExpiredHandler(null);
  }, [clearAll]);

  const me = query.data ?? null;
  const status: SessionStatus = query.isPending ? 'loading' : query.isError ? 'error' : me ? 'authenticated' : 'anonymous';
  const membership = me?.memberships.find((m) => m.organizationId === me.activeOrganizationId) ?? null;
  const permissions = useMemo<ReadonlySet<Permission>>(() => (membership ? new Set(membership.permissions) : EMPTY), [membership]);

  const setMe = useCallback((next: MeResponse | null) => qc.setQueryData(ME_KEY, next), [qc]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearAll();
    }
  }, [clearAll]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      const next = await authApi.switchOrganization(organizationId);
      // todo dado em cache pertence à loja anterior
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      qc.setQueryData(ME_KEY, next);
    },
    [qc],
  );

  const value = useMemo<Session>(
    () => ({ status, me, membership, permissions, refetch: query.refetch, setMe, logout, switchOrganization }),
    [status, me, membership, permissions, query.refetch, setMe, logout, switchOrganization],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession precisa estar dentro de <SessionProvider>');
  return ctx;
}

/** `usePermission('customers.read')` ou, com lista, true se tiver QUALQUER uma. */
export function usePermission(permission: Permission | Permission[]): boolean {
  const { permissions } = useSession();
  return Array.isArray(permission) ? permission.some((p) => permissions.has(p)) : permissions.has(permission);
}
